import axios from "axios";
import { getTrackingConfig, getSessionId } from "@/constants/tracking.constants";

const GO_API_URL = import.meta.env.REACT_APP_API_GO;

class TrackingService {
	previousUrl = null;
	pageStartTime = Date.now();
	isEnabled = true;
	lastTrackedUrl = null;
	lastTrackTime = 0;
	debounceMs = 1000;

	async trackPageView(pathname) {
		if (!this.isEnabled) return;

		const now = Date.now();
		if (pathname === this.lastTrackedUrl && now - this.lastTrackTime < this.debounceMs) {
			console.log(`[Tracking] Skipping duplicate tracking for: ${pathname}`);
			return;
		}

		const config = getTrackingConfig(pathname);

		if (!config) {
			console.warn(`[Tracking] No config found for path: ${pathname}`);
			return;
		}

		let duration_ms = null;
		if (this.pageStartTime && pathname !== this.lastTrackedUrl) {
			duration_ms = Date.now() - this.pageStartTime;
		}

		const payload = {
			visualization: config.visualization,
			module_general: config.module_general,
			module_specific: config.module_specific,
			sub_module: config.sub_module || null,
			action: config.action || "view",
			url: pathname,
			previous_url: this.previousUrl,
			session_id: getSessionId(),
			duration_ms,
		};

		this.lastTrackedUrl = pathname;
		this.lastTrackTime = now;

		try {
			await axios.post(`${GO_API_URL}/app/tracking`, payload);
		} catch (error) {
			console.error("[Tracking] Error tracking page view:", error);
		}

		this.previousUrl = pathname;
		this.pageStartTime = Date.now();
	}

	async trackAction({ module_general, module_specific, action, sub_module = null, entity_id = null, entity_type = null, visualization = "Cliente" }) {
		if (!this.isEnabled) return;

		const payload = {
			visualization,
			module_general,
			module_specific,
			sub_module,
			action,
			entity_id,
			entity_type,
			url: globalThis.location.pathname,
			session_id: getSessionId(),
		};

		try {
			await axios.post(`${GO_API_URL}/app/tracking`, payload, {
				timeout: 2000,
			});
		} catch (error) {
			console.error("[Tracking] Error tracking action:", error);
		}
	}

	trackCreate(module_general, module_specific, entity_type, entity_id = null) {
		return this.trackAction({
			module_general,
			module_specific,
			action: "create",
			sub_module: "Crear",
			entity_type,
			entity_id,
		});
	}

	trackEdit(module_general, module_specific, entity_type, entity_id) {
		return this.trackAction({
			module_general,
			module_specific,
			action: "edit",
			sub_module: "Editar",
			entity_type,
			entity_id,
		});
	}

	trackDelete(module_general, module_specific, entity_type, entity_id) {
		return this.trackAction({
			module_general,
			module_specific,
			action: "delete",
			sub_module: "Eliminar",
			entity_type,
			entity_id,
		});
	}

	trackExport(module_general, module_specific, export_type = "excel") {
		return this.trackAction({
			module_general,
			module_specific,
			action: "export",
			sub_module: `Exportar ${export_type}`,
		});
	}

	trackSearch(module_general, module_specific, search_term = null) {
		return this.trackAction({
			module_general,
			module_specific,
			action: "search",
			sub_module: "Buscar",
			entity_type: search_term ? "search_term" : null,
		});
	}

	trackFilter(module_general, module_specific) {
		return this.trackAction({
			module_general,
			module_specific,
			action: "filter",
			sub_module: "Filtrar",
		});
	}

	setEnabled(enabled) {
		this.isEnabled = enabled;
	}

	reset() {
		this.previousUrl = null;
		this.pageStartTime = null;
		sessionStorage.removeItem("tracking_session_id");
	}
}

const trackingService = new TrackingService();

if (import.meta.env.DEV && import.meta.env.VITE_DISABLE_TRACKING === "true") {
	trackingService.setEnabled(false);
}

export default trackingService;
